import React, { useState } from "react";
import { X, AlertTriangle, Trash2, Tag } from "lucide-react";
import { Button } from "../ui/button";
import { pipelineApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";
import { toast } from "sonner";

const SOURCES = ["Manual", "Web Form", "Referral", "Phone", "Walk-in", "Demo Request", "Website", "Other"];
const INDUSTRIES = ["HVAC", "Plumbing", "Electrical", "Roofing", "Landscaping", "Pest Control", "Other"];
const SIZES = ["1-5 techs", "6-20 techs", "21-50 techs", "50+ techs"];
const PLANS = ["Starter", "Pro", "Enterprise"];

export default function CustomCardModal({ pipelineId, stages, defaultStageId, customers, users, card, onClose, onSaved, onDeleted, salesMode = false }) {
  const isEdit = !!card;
  const [stageId, setStageId] = useState(card?.stageId || defaultStageId || stages[0]?.id || "");
  const [title, setTitle] = useState(card?.title || "");
  const [customerId, setCustomerId] = useState(card?.customerId || "");
  const [value, setValue] = useState(card?.value != null ? String(card.value) : "");
  const [assigneeId, setAssigneeId] = useState(card?.assigneeId || "");
  const [dueAt, setDueAt] = useState(card?.dueAt ? card.dueAt.slice(0, 10) : "");
  const [tagsInput, setTagsInput] = useState((card?.tags || []).join(", "));
  const [notes, setNotes] = useState(card?.notes || "");
  const [source, setSource] = useState(card?.source || "Manual");
  // Sales-mode fields (only shown when salesMode=true — hidden for tenant boards)
  const [companyName, setCompanyName] = useState(card?.companyName || "");
  const [contactName, setContactName] = useState(card?.contactName || "");
  const [contactEmail, setContactEmail] = useState(card?.contactEmail || "");
  const [contactPhone, setContactPhone] = useState(card?.contactPhone || "");
  const [industry, setIndustry] = useState(card?.industry || "");
  const [companySize, setCompanySize] = useState(card?.companySize || "");
  const [planInterest, setPlanInterest] = useState(card?.planInterest || "");
  const [trialStartsAt, setTrialStartsAt] = useState(card?.trialStartsAt ? card.trialStartsAt.slice(0, 10) : "");
  const [expectedCloseAt, setExpectedCloseAt] = useState(card?.expectedCloseAt ? card.expectedCloseAt.slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Card title is required");
      return;
    }
    setBusy(true);
    setError("");
    const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
    const payload = {
      stageId,
      title: title.trim(),
      customerId: salesMode ? null : (customerId || null),
      customerName: salesMode ? "" : (customers.find((c) => c.id === customerId)?.name || ""),
      value: Number(value) || 0,
      assigneeId: assigneeId || null,
      assigneeName: users.find((u) => u.id === assigneeId)?.name || "",
      dueAt: dueAt || null,
      tags,
      notes,
      source,
    };
    if (salesMode) {
      Object.assign(payload, {
        companyName: companyName || null,
        contactName: contactName || null,
        contactEmail: contactEmail || null,
        contactPhone: contactPhone || null,
        industry: industry || null,
        companySize: companySize || null,
        planInterest: planInterest || null,
        trialStartsAt: trialStartsAt || null,
        expectedCloseAt: expectedCloseAt || null,
      });
    }
    try {
      const saved = isEdit
        ? await pipelineApi.updateCard(card.id, payload)
        : await pipelineApi.createCard({ pipelineId, ...payload });
      onSaved(saved);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save card");
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Are you sure you want to DELETE this card?")) return;
    setBusy(true);
    try {
      await pipelineApi.deleteCard(card.id);
      onDeleted(card.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="pipeline-card-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div className="text-[14px] font-semibold text-slate-900">{isEdit ? "Edit card" : "Add card"}</div>
          <button onClick={onClose} data-testid="card-modal-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3.5" data-testid="card-modal-form">
          <Field label="Title *">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="card-title"
              placeholder="e.g. Roof inspection — Smith residence"
              className={inputCls}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Stage">
              <select value={stageId} onChange={(e) => setStageId(e.target.value)} data-testid="card-stage" className={inputCls}>
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Value ($)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="card-value"
                placeholder="0"
                className={inputCls}
              />
            </Field>
          </div>

          {salesMode ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Company name"><input value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="card-company" placeholder="Acme HVAC" className={inputCls} /></Field>
                <Field label="Contact name"><input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="card-contact-name" placeholder="Jordan Lee" className={inputCls} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact email"><input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="card-contact-email" placeholder="jordan@acme.com" className={inputCls} /></Field>
                <Field label="Contact phone"><input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="card-contact-phone" placeholder="+1 555 000 0000" className={inputCls} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Industry">
                  <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="card-industry" className={inputCls}>
                    <option value="">— pick —</option>
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </Field>
                <Field label="Company size">
                  <select value={companySize} onChange={(e) => setCompanySize(e.target.value)} data-testid="card-size" className={inputCls}>
                    <option value="">— pick —</option>
                    {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Plan interest">
                  <select value={planInterest} onChange={(e) => setPlanInterest(e.target.value)} data-testid="card-plan" className={inputCls}>
                    <option value="">—</option>
                    {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Trial starts"><input type="date" value={trialStartsAt} onChange={(e) => setTrialStartsAt(e.target.value)} data-testid="card-trial-starts" className={inputCls} /></Field>
                <Field label="Expected close"><input type="date" value={expectedCloseAt} onChange={(e) => setExpectedCloseAt(e.target.value)} data-testid="card-expected-close" className={inputCls} /></Field>
              </div>
            </>
          ) : (
            <Field label="Customer">
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="card-customer" className={inputCls}>
                <option value="">— No customer linked —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Assignee">
              <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} data-testid="card-assignee" className={inputCls}>
                <option value="">— Unassigned —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </Field>
            <Field label="Due date">
              <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="card-due" className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Source">
              <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="card-source" className={inputCls}>
                {SOURCES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Tags (comma separated)">
              <div className="relative">
                <Tag className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  data-testid="card-tags"
                  placeholder="VIP, Urgent"
                  className={inputCls + " pl-8"}
                />
              </div>
            </Field>
          </div>

          <Field label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="card-notes"
              rows={3}
              placeholder="Add context, next steps…"
              className={inputCls + " py-2 h-auto resize-y"}
            />
          </Field>

          {error && (
            <div data-testid="card-modal-error" className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            {isEdit ? (
              <Button type="button" variant="outline" onClick={remove} disabled={busy} data-testid="card-delete-btn" className="h-9 text-[13px] text-rose-600 border-rose-200 hover:bg-rose-50">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
              </Button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="card-save-btn" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
                {busy ? "Saving…" : isEdit ? "Save changes" : "Add card"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
